(ns manage.adset.ocp.hooks
  (:require ["moment/min/moment-with-locales$default" :as moment]
            ["react-hook-form" :as rhf]
            [cljs-bean.core :refer [->clj]]
            [clojure.string :as str]
            [common.flags :refer [use-flag]]
            [common.form :as form]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.adset :as q]
            [common.resource.query.social-settings :as social-settings]
            [common.util :as util]
            [lemonpi.specs.interface.adset :as adset-spec]
            [malli.core :as m]
            [manage.adset.business-logic :as business-logic]
            [manage.adset.util :as adset-util]
            [re-frame.core :as rf]
            [uix.core :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(def ^:private valid-channels
  (set (map name (keys business-logic/channels))))

(def ^:private step1-schema
  (m/schema
   [:map
    [:name
     [:string {:min 1 :max 150
               :error/fn (fn [{:keys [value]} _]
                           (cond
                             (str/blank? value) "Please input adset name"
                             (> (count value) 150) "Name can not be longer than 150 characters"
                             :else nil))}]]
    [:channel
     [:fn {:error/message "Please select a channel"}
      (fn [v] (contains? valid-channels v))]]
    [:workflow
     [:enum {:error/message "Please select a workflow"}
      "ad-variants" "content-variants" "rules"]]]))

(def ^:private step2-schema
  (m/schema
   [:and
    [:map
     [:plannedDates {:optional false} [:fn {:error/message "Please select a start and an end date"}
                                       (fn [v] (and (some? (first v))
                                                    (some? (second v))))]]
     [:targetImpressions [:int {:min 1
                                :error/message "Please input expected amount of impressions"}]]
     [:countries {:optional true} [:sequential :any]]]]))

(def ^:private step2-schema-ctv
  (m/schema
   [:and
    [:map
     [:plannedDates {:optional false} [:fn {:error/message "Please select a start and an end date"}
                                       (fn [v] (and (some? (first v))
                                                    (some? (second v))))]]
     [:targetImpressions [:int {:min 1
                                :error/message "Please input expected amount of impressions"}]]
     [:countries [:fn {:error/message "Please select a region"}
                  (fn [v] (= 1 (count v)))]]]]))

(uix/defhook use-workflow-options [channel]
  (let [ctv-with-ad-variants-enabled? (use-flag :ctv-with-ad-variants)
        ;; TEMP (local-dev repro only, do not commit): force on since fresh
        ;; local-dev test advertisers aren't targeted by this LD flag.
        legacy-workflows-enabled? true #_(use-flag :legacy-workflows)
        workflows (get-in business-logic/channels [(keyword channel) :workflows])
        workflow-entries
        (when (and channel (not= channel "") (seq workflows))
          (cond->> workflows
            (not legacy-workflows-enabled?) (filter (comp #{:ad-variants} first))

            (and legacy-workflows-enabled? (= (keyword channel) :ctv) (not ctv-with-ad-variants-enabled?))
            (remove (comp #{:ad-variants} first))))]
    (mapv (fn [[key m]] (assoc m :value (name key))) workflow-entries)))

(uix/defhook use-channel-options []
  (let [tiktok-enabled? (use-flag :tiktok-channel)
        youtube-enabled? (use-flag :youtube-with-ad-variants)]
    (cond->> adset-util/channel-options
      (not tiktok-enabled?) (remove (fn [{:keys [value]}] (= value "tiktok")))
      (not youtube-enabled?) (remove (fn [{:keys [value]}] (= value "youtube"))))))

(defn- id-input-option-rows [fallback-ids]
  (mapv (fn [id] {:id (str id)}) fallback-ids))

(uix/defhook use-adset-create-meta-fields [control]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {rows :data accounts-loading? :isFetching} (use-query (social-settings/social-settings-query advertiser-id))
        rows (or rows [])
        account-options (mapv #(let [i (str (:id %))] {:value i :label (str (:description %) " - " i)}) rows)
        {:keys [facebookAdAccountId facebookPageId instagramUserId]} (->clj (rhf/useWatch #js {:control control}))
        has-acct? (not (str/blank? facebookAdAccountId))
        selected (some #(when (= (str (:id %)) facebookAdAccountId) %) rows)
        fb-options (id-input-option-rows (:meta-page-ids selected))
        ig-options (id-input-option-rows  (:meta-instagram-user-ids selected))
        {:keys [setValue]} (->clj (rhf/useFormContext))
        {campaigns-data :data campaigns-loading? :isFetching}
        (use-query (social-settings/fetch-meta-campaigns-by-account-query
                    {:advertiser-id advertiser-id
                     :meta-ad-account-id facebookAdAccountId}))]
    (uix/use-effect
     (fn []
       (if-not has-acct?
         (do (setValue "facebookPageId" "") (setValue "instagramUserId" ""))
         (doseq [[field cur opts] [["facebookPageId" facebookPageId fb-options]
                                   ["instagramUserId" instagramUserId ig-options]]]
           (let [c (str cur)]
             (cond
               (empty? opts) (setValue field "")
               (or c (not-any? #(= c (str (:id %))) opts))
               (setValue field (str (:id (first opts)))))))))
     [has-acct? fb-options ig-options facebookPageId instagramUserId setValue])
    {:account-options account-options
     :accounts-loading? accounts-loading?
     :facebook-options fb-options
     :instagram-options ig-options
     :account-selected? has-acct?
     :campaigns-data campaigns-data
     :campaigns-loading? campaigns-loading?}))

(defn country-options
  "Returns country options for the given channel: [{:value k :label \"...\"} ...] sorted by label."
  [channel]
  (let [channel       (keyword channel)
        country-codes (get-in business-logic/channels [channel :countries])]
    (->> (select-keys adset-spec/country-code-to-country-name country-codes)
         (mapv (fn [[k v]]
                 {:value k
                  :label (->> (str/split v #"\s+")
                              (map str/capitalize)
                              (str/join " "))}))
         (sort-by :label))))

(defn- initial-default-values []
  #js {:name ""
       :channel ""
       :workflow ""
       :countries #js []
       :plannedDates #js [(moment) nil]
       :targetImpressions nil
       :facebookAdAccountId ""
       :facebookPageId ""
       :instagramUserId ""
       :facebookCampaignId ""
       :autoEnable true
       :autoApproveAdVariants false
       :autoPublishing false})

(uix/defhook use-adset-create-form []
  (let [[step set-step] (uix/use-state :step1)
        step-ref (uix/use-ref step)
        automated-activation? (use-flag :automated-activation)
        {:keys [campaign-id] :as url-context} (use-subscribe [:url-context])

        default-values (uix/use-memo (fn [] (initial-default-values)) [])

        step1-resolver (form/use-malli-resolver step1-schema)
        step2-resolver (form/use-malli-resolver step2-schema)
        step2-ctv-resolver (form/use-malli-resolver step2-schema-ctv)
        resolver (uix/use-callback
                  (fn [data]
                    (let [step* @step-ref
                          resolver (cond
                                     (= step* :step1) step1-resolver
                                     (not= (.-channel data) "ctv") step2-resolver
                                     :else step2-ctv-resolver)]
                      (resolver data)))

                  [step1-resolver step2-resolver step2-ctv-resolver])

        form-control (form/use-form #js {:mode "onSubmit"
                                         :defaultValues default-values
                                         :resolver resolver})
        {:keys [handleSubmit control setValue]} (->clj form-control)
        channel (rhf/useWatch #js {:control control :name "channel"})
        workflow-options (use-workflow-options channel)

        {create-adset :mutate is-submitting? :isPending}
        (use-mutation (q/create-adset-mutation {:campaign-id campaign-id}))

        {save-integration-settings :mutate}
        (use-mutation (social-settings/save-integration-settings-mutation
                       {:advertiser-id (:advertiser-id url-context)}))

        save-integration-settings! (uix/use-callback
                                    (fn [adset-id channel data]
                                      (let [payload (util/filter-nils-map
                                                     (cond-> {}
                                                       (= channel "meta")
                                                       (merge {:facebook-ad-account-id (:facebookAdAccountId data)
                                                               :facebook-page-id (:facebookPageId data)
                                                               :instagram-user-id (:instagramUserId data)
                                                               :auto-enable (:autoEnable data)
                                                               :integration-method "direct"})
                                                       (and (= channel "meta")
                                                            (not-empty (:facebookCampaignId data)))
                                                       (assoc :facebook-campaign-ids [(:facebookCampaignId data)])
                                                       (= channel "tiktok")
                                                       (assoc :auto-enable (:autoEnable data))
                                                       automated-activation?
                                                       (assoc :auto-publish (:autoPublishing data)))
                                                     :remove-blank-strings? true)]
                                        (when (seq payload)
                                          (save-integration-settings
                                           (merge payload {:adset-id adset-id :channel channel})
                                           #js {:onSuccess (fn [_response]
                                                             (rf/dispatch [:response-handler/handle-response
                                                                           {:entity-name "Adset integration settings"
                                                                            :type :success
                                                                            :message "Successfully saved integration settings"}]))
                                                :onError (fn [response]
                                                           (rf/dispatch [:response-handler/handle-response
                                                                         {:entity-name "Adset integration settings"
                                                                          :type :error
                                                                          :message "Failed to save integration settings"
                                                                          :response response}]))}))))
                                    [url-context automated-activation? save-integration-settings])

        submit! (uix/use-callback
                 (fn [data]
                   (create-adset (business-logic/to-adset-spec2 data)
                                 #js {:onSuccess (fn [^js response]
                                                   (let [adset-id (.-id response)
                                                         ch (:channel data)]
                                                     (save-integration-settings! adset-id ch data)
                                                     (rf/dispatch [:ocp/close])
                                                     (adset-util/navigate-to-landing-page
                                                      (.-workflow response)
                                                      (assoc url-context :adset-id adset-id))))}))
                 [create-adset save-integration-settings! url-context])

        on-submit (handleSubmit (fn [^js data]
                                  (let [data-clj (->clj data)]
                                    (if (= step :step1)
                                      (set-step :step2)
                                      (submit! data-clj)))))

        on-back (uix/use-callback (fn [] (set-step :step1)) [])]
    (uix/use-effect (fn [] (reset! step-ref step)) [step])
    (uix/use-effect (fn []
                      (when (seq workflow-options)
                        (setValue "workflow" (business-logic/auto-select-workflow workflow-options))))
                    [channel setValue workflow-options])
    {:step step
     :on-submit on-submit
     :on-back on-back
     :control control
     :form-control form-control
     :workflow-options workflow-options
     :is-submitting? is-submitting?}))
